"use client";
import { useState } from "react";
import { motion } from "framer-motion";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";

const testimonials = [
  {
    name: "Marcus Johnson",
    country: "United States 🇺🇸",
    stars: 5,
    quote:
      "I started with the Crypto Mining plan six months ago. My returns have been consistent every month — exactly as advertised. MineMe is the real deal.",
    plan: "Crypto Mining Plan",
    invested: "$8,000",
  },
  {
    name: "Aisha Rahman",
    country: "United Arab Emirates 🇦🇪",
    stars: 5,
    quote:
      "The Bullion Trade plan gave me confidence because I understand the gold market. The stop-loss feature gave me even more peace of mind. Highly recommend.",
    plan: "Bullion Trade Plan",
    invested: "$15,000",
  },
  {
    name: "David Okonkwo",
    country: "Nigeria 🇳🇬",
    stars: 5,
    quote:
      "The referral system alone has been incredible. I brought in my team and the commissions just keep stacking. MineMe has changed my financial life.",
    plan: "Referral Program",
    invested: "$3,200",
  },
  {
    name: "Lena Müller",
    country: "Germany 🇩🇪",
    stars: 5,
    quote:
      "I was sceptical at first, but after the first payout I was sold. The platform is transparent, professional, and the support team is excellent.",
    plan: "Pharmaceuticals Plan",
    invested: "$20,000",
  },
  {
    name: "Carlos Reyes",
    country: "Mexico 🇲🇽",
    stars: 5,
    quote:
      "Used the calculator before investing and the actual returns matched almost exactly. That level of accuracy speaks to MineMe's integrity.",
    plan: "Crypto Mining Plan",
    invested: "$5,500",
  },
];

function StarRating({ count }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          size={14}
          fill={i < count ? "#F59E0B" : "none"}
          color={i < count ? "#F59E0B" : "var(--border)"}
        />
      ))}
    </div>
  );
}

export default function Testimonials() {
  const [current, setCurrent] = useState(0);

  const prev = () =>
    setCurrent((c) => (c - 1 + testimonials.length) % testimonials.length);
  const next = () => setCurrent((c) => (c + 1) % testimonials.length);

  const visible = [
    testimonials[current],
    testimonials[(current + 1) % testimonials.length],
    testimonials[(current + 2) % testimonials.length],
  ];

  return (
    <section
      id="testimonials"
      className="bg-bg-primary 2xl:px-6 xl:px-5 lg:px-4 md:px-4  px-4 2xl:py-24 xl:py-20 lg:py-16 md:py-12 py-10"
    >
      <div className="mx-auto max-w-[1200px]">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-12 text-center sm:mb-16"
        >
          <span className="mb-4 inline-block rounded-full border border-[rgba(245,158,11,0.25)] bg-[rgba(245,158,11,0.1)] px-4 py-1.5 text-xs font-medium text-accent-amber sm:text-sm">
            Investor Stories
          </span>
          <h2 className="font-display mb-3 text-[clamp(28px,5vw,52px)] leading-tight text-text-primary">
            What Our Investors <span className="text-ember">Say</span>
          </h2>
          <p className="text-sm text-text-muted sm:text-lg">
            Real results from real members across the globe.
          </p>
        </motion.div>

        <div className="mb-10 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
          {visible.map((t, i) => (
            <motion.div
              key={current + i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.07 }}
              className={`card-base relative p-4 2xl:p-6 xl:p-6 lg:p-5 md:p-5 sm:p-4 ${i > 0 ? "hidden md:block" : ""} ${i > 1 ? "hidden xl:block" : ""}`}
            >
              <Quote
                size={24}
                color="rgba(245,158,11,0.2)"
                className="mb-4 sm:size-7"
              />

              <p className="mb-6 text-[14px] italic leading-relaxed text-text-muted sm:text-[15px] sm:leading-7">
                &ldquo;{t.quote}&rdquo;
              </p>

              <div className="border-t border-border pt-5">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-display mb-1 text-[14px] font-bold sm:text-[15px]">
                      {t.name}
                    </div>
                    <div className="mb-2 text-[11px] text-text-muted sm:text-sm">
                      {t.country}
                    </div>
                    <StarRating count={t.stars} />
                  </div>
                  <div className="text-right">
                    <div className="mb-1 text-[10px] uppercase tracking-[0.06em] text-text-muted sm:text-[11px]">
                      Plan
                    </div>
                    <div className="text-[11px] font-semibold text-accent-amber sm:text-xs">
                      {t.plan}
                    </div>
                    <div className="mt-1 text-[11px] text-text-muted sm:text-xs">
                      Invested {t.invested}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="flex items-center justify-center gap-4">
          <button
            onClick={prev}
            className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-full border border-border bg-bg-secondary text-text-primary transition hover:border-accent-amber hover:text-accent-amber"
          >
            <ChevronLeft size={18} />
          </button>

          <div className="flex gap-2">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                className={`h-2 cursor-pointer rounded border-0 p-0 transition-all ${
                  i === current
                    ? "w-6 bg-accent-amber"
                    : "w-2 bg-border hover:bg-text-muted"
                }`}
              />
            ))}
          </div>

          <button
            onClick={next}
            className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-full border border-border bg-bg-secondary text-text-primary transition hover:border-accent-amber hover:text-accent-amber"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
}
